import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Star,
  Users,
  TrendingUp,
  Award,
  BookOpen,
  Brain,
  MapPin,
  Phone,
  Clock,
  Zap,
  Shield,
  Target,
  ChevronRight,
  GraduationCap,
  ArrowRight,
  FileText,
  BarChart2,
  ClipboardList,
} from "lucide-react";
import FAQAccordion from "../components/FAQAccordion";

/* ─── constants ─────────────────────────────────────────────────────── */
const PHONE = "+919918519991";
const WA_MSG = encodeURIComponent(
  "Hello SSS Achievers, I found you while searching for CBSE coaching in Ghazipur. Please share course details. Thank you."
);
const WA_URL = `https://wa.me/919918519991?text=${WA_MSG}`;

const fade = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.5 },
  }),
};

/* ─── data ──────────────────────────────────────────────────────────── */
const STATS = [
  { value: "340+", label: "CBSE Students Enrolled", icon: Users },
  { value: "95%", label: "Board Success Rate", icon: TrendingUp },
  { value: "15+", label: "Expert CBSE Educators", icon: GraduationCap },
  { value: "8–12", label: "Classes We Teach", icon: BookOpen },
];

const CBSE_CLASSES = [
  { cls: "Class 8", badge: "8th", desc: "NCERT-based foundation, all subjects, school exam prep", color: "bg-blue-50 border-blue-200 text-blue-700", to: "/courses/class-8-foundation" },
  { cls: "Class 9", badge: "9th", desc: "CBSE curriculum, concept clarity, board readiness begins", color: "bg-emerald-50 border-emerald-200 text-emerald-700", to: "/courses/class-9-foundation" },
  { cls: "Class 10", badge: "10th", desc: "CBSE board exam preparation — Maths, Science, SST, English", color: "bg-amber-50 border-amber-200 text-amber-700", to: "/courses/class-10-board-preparation" },
  { cls: "Class 11", badge: "11th", desc: "Science & Commerce streams — PCM, PCB, all core subjects", color: "bg-violet-50 border-violet-200 text-violet-700", to: "/courses/class-11-foundation" },
  { cls: "Class 12", badge: "12th", desc: "CBSE board mastery — all streams, full mock tests, exam strategy", color: "bg-rose-50 border-rose-200 text-rose-700", to: "/courses/class-12-board-preparation" },
];

const CBSE_STRATEGY = [
  { icon: FileText, title: "NCERT-First Approach", desc: "Our teaching is 100% aligned with NCERT — the primary source of CBSE board questions. We master every chapter before moving to extra practice." },
  { icon: ClipboardList, title: "CBSE Exam Pattern Mastery", desc: "Students practice every question type — MCQs, short answer, long answer, case studies and map/diagram questions — exactly as per CBSE board format." },
  { icon: BarChart2, title: "Previous Year Paper Analysis", desc: "We analyse 10 years of CBSE board papers to identify high-frequency topics, mark distributions and commonly tested concepts for focused preparation." },
  { icon: Target, title: "Answer Writing Technique", desc: "Board answers require structure. We train students in the exact format examiners expect — keywords, diagrams, stepwise solutions and time allocation." },
  { icon: Brain, title: "Concept Clarity Before Memory", desc: "We never promote rote learning. Every CBSE topic is explained conceptually so students can answer any twist in board questions confidently." },
  { icon: Zap, title: "Full Syllabus Mock Tests", desc: "3-hour full board mock exams with CBSE marking scheme, followed by detailed analysis and personalised feedback to improve before the actual exam." },
  { icon: Shield, title: "Revision Strategy", desc: "Structured revision timetable for each subject, chapter-wise quick notes, formula sheets and last-minute exam tips shared before boards." },
  { icon: Award, title: "Subject-Wise Expert Faculty", desc: "Each CBSE subject is taught by a dedicated subject specialist — no general teachers covering multiple subjects superficially." },
];

const TESTIMONIALS = [
  {
    name: "Priya Sharma",
    cls: "Class 10 CBSE, 2025",
    rating: 5,
    text: "SSS Achievers' CBSE coaching in Ghazipur is outstanding. The NCERT-first approach and board mock tests helped me score 94% overall. Best CBSE coaching centre!",
  },
  {
    name: "Aman Tiwari",
    cls: "Class 12 CBSE, 2025",
    rating: 5,
    text: "The previous year paper analysis sessions at SSS Achievers gave me an edge. I knew exactly what to expect in the board exam. Scored 91% in Class 12 boards!",
  },
  {
    name: "Meera Singh (Parent)",
    cls: "Parent of Class 10 Student",
    rating: 5,
    text: "The answer-writing technique training at SSS Achievers made a huge difference. My daughter's board answers improved dramatically. Highly recommended for CBSE students in Ghazipur.",
  },
  {
    name: "Vivek Yadav",
    cls: "Class 9, 2025",
    rating: 5,
    text: "SSS Achievers follows CBSE curriculum perfectly. The faculty explains every NCERT chapter in depth. I'm confident about Class 10 boards already. Best CBSE coaching in Ghazipur!",
  },
];

const COMPARISON = [
  { feature: "CBSE Alignment", sss: "100% NCERT + CBSE pattern", others: "Generic textbook approach" },
  { feature: "Board Mock Tests", sss: "Full 3-hour board mocks", others: "Short quizzes only" },
  { feature: "Answer Writing Training", sss: "Dedicated CBSE format training", others: "Not systematically done" },
  { feature: "PYQ Analysis", sss: "10 years of board papers covered", others: "1–2 years at best" },
  { feature: "Batch Size", sss: "Small Batch (focused)", others: "50+ students (crowded)" },
  { feature: "Revision Strategy", sss: "Structured revision plan + sheets", others: "Self-managed" },
  { feature: "Demo Class", sss: "Free before enrollment", others: "Usually not offered" },
];

/* ─── component ─────────────────────────────────────────────────────── */
export default function CBSECoachingGhazipur() {
  return (
    <>
      <Helmet>
        <title>CBSE Coaching in Ghazipur | Best CBSE Coaching Institute – SSS Achievers</title>
        <meta
          name="description"
          content="Best CBSE coaching in Ghazipur for Class 8–12 at SSS Achievers. NCERT-first teaching, board mock tests, answer-writing training, 95% success rate. Free demo class. Enroll today!"
        />
        <meta
          name="keywords"
          content="CBSE coaching in Ghazipur, CBSE coaching Ghazipur, best CBSE coaching Ghazipur, CBSE board coaching Ghazipur UP, CBSE class 10 coaching Ghazipur, CBSE class 12 coaching Ghazipur, NCERT coaching Ghazipur, board exam coaching Ghazipur, SSS Achievers CBSE, CBSE tuition Ghazipur"
        />
        <link rel="canonical" href="https://www.sssachievers.com/cbse-coaching-ghazipur" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="CBSE Coaching in Ghazipur | Best Board Coaching – SSS Achievers" />
        <meta property="og:description" content="Ghazipur's best CBSE coaching for Class 8–12. NCERT-first teaching, board mock tests, 95% success rate, free demo class at SSS Achievers." />
        <meta property="og:url" content="https://www.sssachievers.com/cbse-coaching-ghazipur" />
        <meta property="og:image" content="https://www.sssachievers.com/logo512.png" />
        <meta property="og:locale" content="en_IN" />
        <meta property="og:site_name" content="SSS Achievers" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="CBSE Coaching in Ghazipur | SSS Achievers" />
        <meta name="twitter:description" content="Best CBSE board coaching in Ghazipur. NCERT-aligned, 95% success rate, free demo at SSS Achievers." />
        <meta name="twitter:image" content="https://www.sssachievers.com/logo512.png" />
        <meta name="geo.region" content="IN-UP" />
        <meta name="geo.placename" content="Ghazipur, Uttar Pradesh" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "EducationalOrganization",
                "@id": "https://www.sssachievers.com/#organization",
                "name": "SSS Achievers",
                "alternateName": ["Best CBSE Coaching Ghazipur", "CBSE Board Coaching Ghazipur"],
                "description": "SSS Achievers is the best CBSE coaching institute in Ghazipur offering NCERT-aligned coaching for Class 8–12 with a 95% board success rate.",
                "url": "https://www.sssachievers.com",
                "logo": "https://www.sssachievers.com/logo512.png",
                "telephone": "+919918519991",
                "address": {
                  "@type": "PostalAddress",
                  "streetAddress": "96/B, Badi Bagh",
                  "addressLocality": "Ghazipur",
                  "addressRegion": "Uttar Pradesh",
                  "postalCode": "233001",
                  "addressCountry": "IN"
                },
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": 25.5825,
                  "longitude": 83.5805
                },
                "aggregateRating": {
                  "@type": "AggregateRating",
                  "ratingValue": "4.9",
                  "reviewCount": "340",
                  "bestRating": "5"
                }
              },
              {
                "@type": "FAQPage",
                "mainEntity": [
                  {
                    "@type": "Question",
                    "name": "Which is the best CBSE coaching in Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers is the best CBSE coaching institute in Ghazipur. With 100% NCERT-aligned teaching, full board mock tests, dedicated answer-writing training, small, focused batches and a 95% board success rate, SSS Achievers is Ghazipur's #1 CBSE coaching centre for Class 8–12."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers follow the CBSE curriculum completely?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers follows a 100% NCERT-first approach for all classes from 8 to 12. Every chapter is taught as per the CBSE curriculum, complemented by board-pattern question practice, case studies and previous year board paper analysis."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Does SSS Achievers conduct CBSE board mock tests?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes. SSS Achievers conducts full 3-hour CBSE board mock tests following the official marking scheme. After each mock test, students receive detailed performance analysis and personalised feedback to improve before the actual board exam."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "Is there a free demo class for CBSE coaching at SSS Achievers?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "Yes — SSS Achievers offers a completely free demo class before enrollment. You can attend a CBSE coaching session, experience the teaching quality and meet the faculty before making any decision."
                    }
                  },
                  {
                    "@type": "Question",
                    "name": "What subjects does CBSE coaching cover at SSS Achievers Ghazipur?",
                    "acceptedAnswer": {
                      "@type": "Answer",
                      "text": "SSS Achievers covers all major CBSE subjects — Mathematics, Science (Physics, Chemistry, Biology), Social Science, English, Computer Science and Commerce subjects for Class 8 through 12, both Science and Commerce streams."
                    }
                  }
                ]
              },
              {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sssachievers.com/" },
                  { "@type": "ListItem", "position": 2, "name": "CBSE Coaching in Ghazipur", "item": "https://www.sssachievers.com/cbse-coaching-ghazipur" }
                ]
              }
            ]
          }
        `}</script>
      </Helmet>

      <main className="bg-white text-slate-900">
        {/* ══ HERO ══ */}
        <section className="relative overflow-hidden bg-gradient-to-br from-[#0097b2]/8 via-white to-[#fcbf0a]/8 pt-10 pb-14 md:pt-20 md:pb-24">
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#0097b2]/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-72 h-72 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav aria-label="breadcrumb" className="mb-6 flex items-center gap-2 text-sm text-slate-500">
              <Link to="/" className="hover:text-[#0097b2] transition">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0097b2] font-medium">CBSE Coaching in Ghazipur</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 items-center">
              <motion.div initial="hidden" animate="visible" variants={fade} className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#fcbf0a]/20 border border-[#fcbf0a]/40">
                  <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                  <span className="text-sm font-semibold text-amber-700">Ghazipur's #1 CBSE Coaching Centre</span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold leading-tight text-slate-900">
                  Best <span className="text-[#0097b2]">CBSE Coaching</span> in Ghazipur — Class 8 to 12
                </h1>

                <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
                  SSS Achievers is Ghazipur's most trusted CBSE coaching institute. NCERT-first teaching, full board mock tests, answer-writing technique training and small, focused batches ensure every student excels in their CBSE board exams.
                </p>

                <div className="flex flex-col sm:flex-row gap-3">
                  <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] shadow-md hover:shadow-lg transition">
                    Book Free Demo CBSE Class <ArrowRight className="w-4 h-4" />
                  </a>
                  <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-6 py-3.5 border-2 border-[#0097b2] text-[#0097b2] font-semibold rounded hover:bg-[#0097b2]/5 transition">
                    <Phone className="w-4 h-4" /> Call: +91 99185 19991
                  </a>
                </div>

                <div className="flex flex-wrap gap-4 pt-2">
                  {["100% NCERT Aligned", "Board Mock Tests", "Answer Writing Training", "95% Success Rate"].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-sm text-slate-600">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2]" /> {t}
                    </span>
                  ))}
                </div>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="grid grid-cols-2 gap-4">
                {STATS.map(({ value, label, icon: Icon }) => (
                  <div key={label} className="bg-white rounded-2xl p-6 shadow-md border border-gray-100 flex flex-col items-center text-center hover:shadow-lg transition">
                    <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-3">
                      <Icon className="w-6 h-6 text-[#0097b2]" />
                    </div>
                    <span className="text-3xl font-bold text-[#0097b2]">{value}</span>
                    <span className="text-sm text-slate-500 mt-1 font-medium">{label}</span>
                  </div>
                ))}
                <div className="col-span-2 bg-gradient-to-r from-[#0097b2] to-[#006f85] rounded-2xl p-5 text-white flex items-center gap-4">
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[1,2,3,4,5].map((s) => <Star key={s} className="w-5 h-5 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                    </div>
                    <p className="font-bold text-lg">4.9 / 5.0 Rating</p>
                    <p className="text-blue-100 text-sm">Based on 340+ CBSE student reviews</p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-blue-100 text-xs uppercase tracking-wider font-semibold">Board</p>
                    <p className="text-3xl font-bold">CBSE</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ CBSE BOARD STRATEGY ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                Our <span className="text-[#0097b2]">CBSE Board Exam Strategy</span> at SSS Achievers
              </motion.h2>
              <p className="mt-4 text-slate-600 max-w-2xl mx-auto text-base">A proven 8-step system that takes students from understanding concepts to scoring high in CBSE board exams.</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {CBSE_STRATEGY.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="group p-6 rounded-2xl border-2 border-gray-100 hover:border-[#0097b2]/40 hover:shadow-lg transition-all duration-300 bg-white">
                  <div className="w-12 h-12 rounded-xl bg-[#0097b2]/10 flex items-center justify-center mb-4 group-hover:bg-[#0097b2]/20 transition-colors">
                    <Icon className="w-6 h-6 text-[#0097b2]" />
                  </div>
                  <h3 className="font-bold text-slate-800 mb-2 text-base">{title}</h3>
                  <p className="text-slate-500 text-sm leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ CLASSES OFFERED ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-[#fcbf0a]/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                CBSE Coaching for <span className="text-[#0097b2]">Class 8 to 12</span> in Ghazipur
              </motion.h2>
              <p className="mt-3 text-slate-600">Complete CBSE coaching across all classes — Science and Commerce streams</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {CBSE_CLASSES.map(({ cls, badge, desc, color, to }, i) => (
                <motion.div key={cls} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}>
                  <Link to={to} onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                    className={`group block p-6 rounded-2xl border-2 ${color} bg-white hover:shadow-xl transition-all duration-300`}>
                    <div className="flex items-start justify-between mb-4">
                      <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${color}`}>{badge}</span>
                      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#0097b2] group-hover:translate-x-1 transition-all" />
                    </div>
                    <h3 className="font-bold text-lg text-slate-800 mb-2">{cls} CBSE Coaching</h3>
                    <p className="text-slate-500 text-sm">{desc}</p>
                    <div className="mt-4 flex items-center gap-1 text-xs font-semibold text-[#0097b2]">
                      View Course <ArrowRight className="w-3 h-3" />
                    </div>
                  </Link>
                </motion.div>
              ))}
              <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={5} variants={fade}
                className="bg-gradient-to-br from-[#0097b2] to-[#006f85] rounded-2xl p-6 text-white flex flex-col justify-center">
                <GraduationCap className="w-8 h-8 text-white/80 mb-3" />
                <h3 className="font-bold text-xl mb-2">Need Help Choosing?</h3>
                <p className="text-blue-100 text-sm mb-4">Our academic counsellors will guide you to the right CBSE course for your child's class and goals.</p>
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-[#0097b2] bg-white px-4 py-2 rounded-lg hover:bg-blue-50 transition w-fit">
                  Talk to Counsellor <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ COMPARISON ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                SSS Achievers CBSE vs. Other Coaching Centres in Ghazipur
              </motion.h2>
            </div>
            <div className="overflow-hidden rounded-2xl border-2 border-gray-100 shadow-sm">
              <div className="grid grid-cols-3 bg-[#0097b2] text-white text-sm font-bold">
                <div className="px-5 py-4">Feature</div>
                <div className="px-5 py-4 text-center border-l border-white/20">SSS Achievers ✓</div>
                <div className="px-5 py-4 text-center border-l border-white/20 text-blue-100">Others</div>
              </div>
              {COMPARISON.map(({ feature, sss, others }, i) => (
                <div key={feature} className={`grid grid-cols-3 text-sm border-b border-gray-100 ${i % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                  <div className="px-5 py-4 font-semibold text-slate-700">{feature}</div>
                  <div className="px-5 py-4 text-center border-l border-gray-100">
                    <div className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-[#0097b2] flex-shrink-0" />
                      <span className="text-slate-700 font-medium">{sss}</span>
                    </div>
                  </div>
                  <div className="px-5 py-4 text-center border-l border-gray-100 text-slate-400 text-xs">{others}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ TESTIMONIALS ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">
                CBSE Students Who Scored Big with <span className="text-[#0097b2]">SSS Achievers</span>
              </motion.h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {TESTIMONIALS.map(({ name, cls, rating, text }, i) => (
                <motion.div key={name} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.15 }} custom={i} variants={fade}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition flex flex-col">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: rating }).map((_, s) => <Star key={s} className="w-4 h-4 fill-[#fcbf0a] text-[#fcbf0a]" />)}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <p className="font-semibold text-slate-800 text-sm">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{cls}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ CAMPUS GLIMPSE ══ */}
        <section className="py-14 md:py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <motion.h2
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-2xl md:text-3xl font-bold"
              >
                Our CBSE Results & <span className="text-[#0097b2]">Campus — SSS Achievers Ghazipur</span>
              </motion.h2>
              <p className="mt-2 text-slate-500 text-sm">Real CBSE board results. Real classrooms. Real achievers.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  src: "/images/sss%20board%20result%20award%20ceremony.avif",
                  alt: "SSS Achievers CBSE board result award ceremony in Ghazipur — students receiving academic excellence awards for top board exam scores 2025",
                  caption: "🏆 CBSE Board Result Award Ceremony 2025",
                },
                {
                  src: "/images/sss%20board%20result%20celebration.avif",
                  alt: "SSS Achievers CBSE students celebrating board exam results in Ghazipur — top scorers at the best CBSE coaching centre in Ghazipur UP",
                  caption: "🎉 CBSE Result Celebration — Ghazipur",
                },
                {
                  src: "/images/gallery/mock-test.jpg",
                  alt: "SSS Achievers CBSE board mock test session in Ghazipur — students appearing in full 3-hour board mock exam at the best CBSE coaching institute in Ghazipur",
                  caption: "📋 CBSE Full Board Mock Test — SSS Achievers",
                },
              ].map(({ src, alt, caption }, i) => (
                <motion.figure
                  key={caption}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="rounded-2xl overflow-hidden shadow-md group"
                >
                  <img
                    src={src}
                    alt={alt}
                    className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                    width="600"
                    height="224"
                  />
                  <figcaption className="bg-white px-4 py-3 text-sm font-semibold text-slate-700 border-t border-gray-100">
                    {caption}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </section>

        {/* ══ LOCATION ══ */}
        <section className="py-16 md:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="space-y-5">
                <h2 className="text-3xl md:text-4xl font-bold">
                  Visit Ghazipur's Best <span className="text-[#0097b2]">CBSE Coaching Centre</span>
                </h2>
                <p className="text-slate-600">Easily accessible from all areas of Ghazipur. Come visit SSS Achievers and experience the difference in CBSE coaching quality.</p>
                {[
                  { icon: MapPin, text: "96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001" },
                  { icon: Phone, text: "+91 99185 19991", href: `tel:${PHONE}` },
                  { icon: Clock, text: "Mon–Sat: 9:00 AM – 7:00 PM" },
                ].map(({ icon: Icon, text, href }) => (
                  <div key={text} className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0097b2]/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-[#0097b2]" />
                    </div>
                    {href ? <a href={href} className="text-sm text-[#0097b2] font-semibold hover:underline mt-2">{text}</a> : <p className="text-sm text-slate-600 mt-2">{text}</p>}
                  </div>
                ))}
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#0097b2] text-white font-semibold rounded hover:bg-[#007a91] transition shadow-md text-sm">
                  Book Free Demo CBSE Class <ArrowRight className="w-4 h-4" />
                </a>
              </motion.div>

              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="rounded-3xl overflow-hidden border-2 border-gray-100 shadow-lg">
                <div className="bg-[#0097b2] px-6 py-4 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-white" />
                  <span className="text-white font-semibold">SSS Achievers — Ghazipur</span>
                </div>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.750962143519!2d83.56063587575478!3d25.57995091609078!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2bb0bb23b08ac59%3A0x4c04724e0b4e6039!2sSSS%20ACHIEVERS%20Coaching%20Centre%20Ghazipur!5e0!3m2!1sen!2sin!4v1775367597808!5m2!1sen!2sin"
                  width="100%"
                  height="280"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="SSS Achievers CBSE Coaching Ghazipur Location"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-[#0097b2]/5 to-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10">
              <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl font-bold">Frequently Asked Questions</motion.h2>
              <p className="mt-3 text-slate-500 text-sm">About CBSE coaching in Ghazipur at SSS Achievers</p>
            </div>
            <FAQAccordion
              faqs={[
                { q: "Which is the best CBSE coaching institute in Ghazipur?", a: "SSS Achievers is the best CBSE coaching institute in Ghazipur. With 100% NCERT-aligned teaching, full board mock tests, answer-writing training, small batches and a 95% board success rate, SSS Achievers is Ghazipur's top CBSE coaching centre for Class 8–12." },
                { q: "Does SSS Achievers follow the CBSE syllabus completely?", a: "Yes — SSS Achievers follows a 100% NCERT-first, CBSE-aligned curriculum for all classes. Every chapter is taught systematically, supplemented by board-pattern question practice, case studies and previous year board paper analysis." },
                { q: "Does SSS Achievers conduct CBSE board mock tests?", a: "Yes. SSS Achievers conducts full 3-hour CBSE board mock tests following the official marking scheme. Each mock test is followed by detailed performance analysis and personalised feedback to help students improve before the actual boards." },
                { q: "What is the batch size at SSS Achievers for CBSE coaching?", a: "CBSE coaching batches at SSS Achievers are kept intentionally small to ensure individual attention, focused teaching, regular doubt-clearing and personalised feedback for every student." },
                { q: "Does SSS Achievers provide answer-writing training for CBSE boards?", a: "Yes. SSS Achievers provides dedicated answer-writing technique training — teaching students how to structure board answers with the right keywords, diagrams, stepwise solutions and formatting that CBSE examiners expect." },
                { q: "Which CBSE classes does SSS Achievers offer coaching for in Ghazipur?", a: "SSS Achievers offers CBSE coaching for Class 8, 9, 10, 11 and 12 in Ghazipur. Both Science (PCM/PCB) and Commerce streams are covered with specialist subject faculty." },
              ]}
            />
          </div>
        </section>

        {/* ══ FINAL CTA ══ */}
        <section className="py-16 md:py-20 bg-gradient-to-r from-[#0097b2] to-[#006f85] relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-16 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-[#fcbf0a]/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20">
                <Star className="w-4 h-4 text-[#fcbf0a] fill-[#fcbf0a]" />
                <span className="text-white text-sm font-semibold">Ghazipur's #1 CBSE Coaching Institute</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white">Score High in CBSE Boards with SSS Achievers</h2>
              <p className="text-blue-100 text-base md:text-lg max-w-2xl mx-auto">
                Limited seats available for CBSE coaching batches. Book your free demo class now and see why 340+ students trust SSS Achievers Ghazipur.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#fcbf0a] text-gray-900 font-bold rounded hover:bg-[#e6ac00] transition shadow-lg text-base">
                  Book Free Demo CBSE Class <ArrowRight className="w-5 h-5" />
                </a>
                <a href={`tel:${PHONE}`} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded hover:bg-white/20 transition text-base">
                  <Phone className="w-5 h-5" /> +91 99185 19991
                </a>
              </div>
              <p className="text-blue-200 text-xs mt-4">📍 96/B, Badi Bagh, Opp. Raj Nursing Home, Ghazipur, UP – 233001</p>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
